import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import moment, {Moment} from 'moment';
import {Alert, Box, Button, Skeleton, Stack, SxProps, Typography} from '@mui/material';
import {DateRangeInput} from '../../../../../../../components/date-range-input/date-range-input';
import GenericHeader from '../../../../../../../components/generic-header/generic-header';
import AttendanceFilterSelect from '../../../../../../../components/attendance-tracker/attendance-filter-select';
import AttendanceRegistryCorrectDialog from '../../../../../../../components/attendance-tracker/attendance-registry-correct-dialog';
import AttendanceRegistryVoidDialog from '../../../../../../../components/attendance-tracker/attendance-registry-void-dialog';
import AttendanceRegistryAddDialog from '../../../../../../../components/attendance-tracker/attendance-registry-add-dialog';
import AttendanceRegistryDetailsDialog from '../../../../../../../components/attendance-tracker/attendance-registry-details-dialog';
import AttendanceRegistryCard from '../../../../../../../components/attendance-tracker/attendance-registry-card';
import {CareServiceAddButton} from '../../../../../../../components/care-services/base/header/care-service-add-button';
import {CareServicesUserInput} from '../../../../../../../components/care-services/base/header/care-services-user-input';
import {RolePermissionType} from '../../../../../../../backend/enums/role-permission-type';
import {UserType} from '../../../../../../../backend/enums/user-type';
import {AffiliatedUser} from '../../../../../../../backend/responses/affiliated-user';
import {
  AttendanceTrackerRegistrySession,
  AttendanceTrackerRegistrySessions,
} from '../../../../../../../backend/responses/attendance-tracker-registry';
import {useAuth} from '../../../../../../../contexts/auth/context';
import {ScreenPaths} from '../../../../../../../enums';
import {useFetchEntityUsers} from '../../../../../../../hooks/use-fetch-entity-users';
import {useServerNow} from '../../../../../../../hooks/use-server-now';
import {translations} from '../../../../../../../i18n/translations';
import {EntityAttendanceTrackerRegistryRepository} from '../../../../../../../repositories/entity-attendance-tracker-registry';
import {filterSessions} from '../../../../../../../utils/functions/attendance-sessions';

const headerSubtitleSx: SxProps = {
  fontSize: '18px',
  fontWeight: 400,
  color: 'white',
  maxWidth: {xs: '250px', sm: '100%'},
};

const contentSx: SxProps = {
  position: 'absolute',
  left: 0,
  right: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  // Space between the header and the first card
  paddingTop: '24px',
};

const FILTER_SECTIONS = [
  {key: 'status', title: 'attendanceStatus', values: ['open', 'closed', 'voided']},
  {key: 'periods', title: 'attendancePeriods', values: ['morning', 'afternoon', 'night']},
] as const;

type FilterKey = (typeof FILTER_SECTIONS)[number]['key'];
type Filters = Record<FilterKey, string[]>;

const NO_FILTERS: Filters = {status: [], periods: []};

export default function AttendanceTrackerRegistryProfessionalCaregiverOfficeScreen() {
  const {t} = useTranslation();
  const {entityId}: {entityId: string} = useParams();
  const {state, hasReadPermission, hasWritePermission} = useAuth();
  const entityName = state.session?.selectedAffiliation?.entity.name;
  const entityPicture = state.session?.selectedAffiliation?.entity.picture;
  const canWrite = hasWritePermission(RolePermissionType.Team_AttendanceTracker_Registry);
  const texts = translations.attendance_tracker_registry_screen;
  const filterTexts = translations.daily_care_log.attendance_tracker.filters;

  // The professionals can only be chosen by who can read the team. The names come with the sessions.
  const canListProfessionals = hasReadPermission(RolePermissionType.Team_Professionals);
  const professionals = useFetchEntityUsers({
    userType: UserType.Caregiver,
    activeUsers: true,
    enabled: canListProfessionals,
  });

  const [startDate, setStartDate] = useState<Moment>(() => moment().startOf('day'));
  const [endDate, setEndDate] = useState<Moment>(() => moment().endOf('day'));
  const [selectedProfessional, setSelectedProfessional] = useState<AffiliatedUser | undefined>(
    undefined
  );
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);

  const [data, setData] = useState<AttendanceTrackerRegistrySessions | undefined>(undefined);
  const [hasError, setHasError] = useState(false);
  // The session whose details are open. It is kept while the dialog closes.
  const [detailsSession, setDetailsSession] = useState<
    AttendanceTrackerRegistrySession | undefined
  >(undefined);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  // The session being voided. It is kept while the dialog closes.
  const [sessionToVoid, setSessionToVoid] = useState<AttendanceTrackerRegistrySession | undefined>(
    undefined
  );
  const [isVoidOpen, setIsVoidOpen] = useState(false);
  // The session being corrected. It is kept while the dialog closes.
  const [sessionToCorrect, setSessionToCorrect] = useState<
    AttendanceTrackerRegistrySession | undefined
  >(undefined);
  const [isCorrectOpen, setIsCorrectOpen] = useState(false);
  const now = useServerNow(data?.serverTime);

  const selectedDay = startDate;
  const isToday = selectedDay.isSame(moment(), 'day');
  const selectedUserId = selectedProfessional?.user.id;

  const loadSessions = useCallback(async () => {
    setData(undefined);
    setHasError(false);

    try {
      setData(
        await EntityAttendanceTrackerRegistryRepository.instance.getSessions(
          entityId,
          startDate.toISOString(),
          endDate.toISOString(),
          selectedUserId
        )
      );
    } catch (e) {
      console.error(e);
      setHasError(true);
    }
  }, [entityId, startDate, endDate, selectedUserId]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const hasFilters = FILTER_SECTIONS.some(({key}) => filters[key].length > 0);

  // The sessions still open are shown first in today, so a forgotten exit is not missed. When filtering,
  // everything is filtered together and ordered by entry.
  const sessions = useMemo(() => {
    if (!data) {
      return undefined;
    }

    const openSessions = isToday ? data.openSessions : [];
    const openIds = new Set(openSessions.map((session) => session.id));
    const others = data.sessions.filter((session) => !openIds.has(session.id));

    if (!hasFilters) {
      return [...openSessions, ...others];
    }

    return filterSessions(
      [...openSessions, ...others],
      {movements: [], status: filters.status, periods: filters.periods},
      selectedDay
    ).sort(
      (a, b) => moment(b.entry.registeredAt).valueOf() - moment(a.entry.registeredAt).valueOf()
    );
  }, [data, isToday, hasFilters, filters, selectedDay]);

  const setFilter = (key: FilterKey, value: string | undefined) =>
    setFilters((current) => ({...current, [key]: value ? [value] : []}));

  const teamUrl = `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}`;
  const registryUrl = `${teamUrl}${ScreenPaths.ProfessionalCaregiver_AttendanceTracker}${ScreenPaths.ProfessionalCaregiver_AttendanceTrackerRegistry}`;

  return (
    <Box sx={{position: 'relative'}}>
      <GenericHeader
        titleCrumbs={[
          {label: t(translations.screen_paths.professional_caregiver_team), link: teamUrl},
          {label: t(translations.teamSideMenu.attendanceTracker.title), link: registryUrl},
          {label: t(translations.teamSideMenu.attendanceTracker.registry), link: registryUrl},
        ]}
        isLoading={false}
        leftColumn={
          <Stack gap={2} sx={{display: 'flex', flexDirection: 'row', padding: 0, width: '100%'}}>
            {canListProfessionals ? (
              <CareServicesUserInput
                usersData={professionals}
                defaultOptionLabel={texts.filters.all_professionals}
                onChange={setSelectedProfessional}
              />
            ) : (
              <Typography sx={headerSubtitleSx} noWrap>
                {t(texts.subtitle)}
              </Typography>
            )}
            {FILTER_SECTIONS.map(({key, title, values}) => (
              <AttendanceFilterSelect
                key={key}
                label={t(key === 'status' ? texts.filters.all_statuses : texts.filters.all_day)}
                options={values.map((value) => ({
                  key: value,
                  label: t(filterTexts.values[value]),
                }))}
                onChange={(value) => setFilter(key, value)}
              />
            ))}
          </Stack>
        }
        rightColumn={
          <Stack direction="row" justifyContent="flex-end" alignItems="center" gap="10px">
            <DateRangeInput
              mode="day"
              startDate={startDate}
              endDate={endDate}
              onChange={(start, end) => {
                setStartDate(start);
                setEndDate(end);
              }}
            />
            {canWrite && <CareServiceAddButton onClick={() => setIsAddOpen(true)} />}
          </Stack>
        }
      />

      <Box sx={contentSx}>
        {hasError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={loadSessions}>
                {t(texts.retry)}
              </Button>
            }
          >
            {t(texts.load_error)}
          </Alert>
        ) : !sessions ? (
          new Array(5)
            .fill(1)
            .map((_item, index) => (
              <Skeleton
                key={`session_skeleton_${index}`}
                variant="rounded"
                animation="wave"
                sx={{width: '100%', height: '82px'}}
              />
            ))
        ) : sessions.length === 0 ? (
          <Typography sx={{textAlign: 'center', marginTop: '40px', color: 'var(--dcm-gray)'}}>
            {t(texts.empty)}
          </Typography>
        ) : (
          sessions.map((session) => (
            <AttendanceRegistryCard
              key={session.id}
              session={session}
              professionalName={session.user.name}
              professionalPicture={session.user.picture}
              now={now}
              onSelect={() => {
                setDetailsSession(session);
                setIsDetailsOpen(true);
              }}
            />
          ))
        )}
      </Box>

      {canWrite && (
        <AttendanceRegistryAddDialog
          open={isAddOpen}
          onClose={() => setIsAddOpen(false)}
          entityId={entityId}
          onRegistered={loadSessions}
        />
      )}

      {canWrite && (
        <AttendanceRegistryCorrectDialog
          open={isCorrectOpen}
          onClose={() => setIsCorrectOpen(false)}
          entityId={entityId}
          session={sessionToCorrect}
          onCorrected={loadSessions}
        />
      )}

      {canWrite && (
        <AttendanceRegistryVoidDialog
          open={isVoidOpen}
          onClose={() => setIsVoidOpen(false)}
          entityId={entityId}
          session={sessionToVoid}
          onVoided={loadSessions}
        />
      )}

      <AttendanceRegistryDetailsDialog
        open={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        session={detailsSession}
        entityName={entityName}
        entityPicture={entityPicture}
        // Nobody voids their own sessions, and a voided session cannot be voided again
        canVoid={
          canWrite &&
          !!detailsSession &&
          !detailsSession.voided &&
          detailsSession.user.id !== state.session?.currentUser?.id
        }
        onVoid={(session) => {
          // The reason is asked in another dialog
          setIsDetailsOpen(false);
          setSessionToVoid(session);
          setIsVoidOpen(true);
        }}
        onCorrect={(session) => {
          // The new data is asked in another dialog
          setIsDetailsOpen(false);
          setSessionToCorrect(session);
          setIsCorrectOpen(true);
        }}
        now={now}
      />
    </Box>
  );
}
